import Helpers from '@Apis/helpers';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { DEFAULT_CUR_ROUND_OFF, QUANTITY } from '@Apis/constants';
import { notification } from 'antd';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import FormHelpers from '@Helpers/FormHelpers';

export const INITIAL_STATE = (user) => ({
  finishedGoods: [
    {
      key: uuidv4(),
      asset1: '',
      product_name: '',
      quantity: '',
      unitPrice: '',
      lot: 0,
      taxId: '',
      discount: 0,
    },
  ],
  trackWastage: true,
  batchLevelJobWorks: true,
  bomByProducts: {},
  extraCharges: {},
  bomLines: {},
  tenantDepartmentId: '',
  paymentTerms: 0,
  scheduledDate: dayjs(),
  deliveryDate: '',
  attachments: [],
  selectedTenant: '',
  currentTab: '/finished-goods',
  showAdvancedSettings: false,
  restrictMoUser: false,
  moUsers: [],
  restrictionCheck: true,
  isIssueSfgEnabled: false,
  visibleColumnsFG: {
    PRODUCT: {
      label: 'Product',
      visible: true,
      disabled: true,
    },
    READY_TO_PRODUCE: {
      label: 'Ready To Produce',
      visible: true,
      disabled: true,
    },
    REQUIRED_QUANTITY: {
      label: 'Required Quantity',
      visible: true,
      disabled: true,
    },
    RM_COST: {
      label: 'Rm Cost Per Unit',
      visible: true,
      disabled: true,
    },
    CHARGES: {
      label: 'Charges Per Unit',
      visible: true,
      disabled: true,
    },
    TOTAL_UNIT_COST: {
      label: 'Total Unit Cost',
      visible: true,
      disabled: true,
    },
    TOTAL_COST: {
      label: 'Total Cost',
      visible: true,
      disabled: true,
    },
  },
  visibleColumnsRM: {
    PRODUCT: {
      label: 'Product',
      visible: true,
      disabled: true,
    },
    CURRENT_STOCK: {
      label: 'Current Stock',
      visible: true,
      disabled: true,
    },
    ESTIMATED_USAGE: {
      label: 'Estimated Usage',
      visible: true,
      disabled: true,
    },
    ESTIMATED_WASTAGE: {
      label: 'Estimated Overage',
      visible: true,
      disabled: true,
    },
    UNIT_COST: {
      label: 'Unit Cost',
      visible: true,
      disabled: true,
    },
    ESTIMATED_TOTAL_COST: {
      label: 'Estimted Total Cost',
      visible: true,
      disabled: true,
    },
  },
  updateDocumentReason: '',
});

export const createBomDataFromProductData = ({
  tenantSku, productData, org, customMessageBillOfMaterial,
}) => {
  const bom = {
    bom_id: uuidv4(),
    created_at: null,
    is_active: true,
    bom_quantity: 0,
    enable_wastage_tracking: true,
    flexible_consumption: 'ALLOWED',
    remarks: null,
    attachments: [],
    bom_number: '',
    product_sku_id: tenantSku?.product_sku_id,
    org_id: productData?.org_id,
    old_org_id: null,
    extra_charges: customMessageBillOfMaterial?.data ?? [],
    assets: [],
    custom_fields: [],
    internal_sku_code: tenantSku?.internal_sku_code,
    product_batch_counter: tenantSku?.product_batch_counter,
    bom_alias_name: '',
    bom_users: null,
    default_yield_percentage: 100,
    is_restricted: false,
    is_issue_sfg_enabled: false,
    ref_product_code: tenantSku?.ref_product_code,
    bom_production_route: {},
    created_by_info: {},
    bom_users_info: {},
    org_info: {
      org_logo: org?.organisation?.[0]?.logo,
      currency_code: org?.organisation?.[0]?.currency_code,
    },
    document_tenant_configuration: {},
    product_info: {
      product_sku_id: tenantSku?.product_info?.product_sku_id,
      product_sku_name: tenantSku?.product_info?.product_sku_name,
      expiry_date_format: tenantSku?.product_info?.expiry_date_format,
      manufacturing_date_format: tenantSku?.product_info?.manufacturing_date_format,
      colour: tenantSku?.product_info?.colour,
      is_active: tenantSku?.product_info?.is_active,
      is_perishable: tenantSku?.product_info?.is_perishable,
      expiry_days: tenantSku?.product_info?.expiry_days,
      uom_id: tenantSku?.product_info?.uom_id,
      uom_info: tenantSku?.product_info?.uom_info,
      internal_sku_code: tenantSku?.product_info?.internal_sku_code,
      product_batch_counter: tenantSku?.product_info?.product_batch_counter,
      description: tenantSku?.product_info?.description,
      product_category_info: tenantSku?.product_category_info,
    },
    tenant_product_info: {
      product_sku_id: tenantSku?.product_sku_id,
      tenant_product_id: tenantSku?.tenant_product_id,
      tenant_id: tenantSku?.tenant_id,
      is_active: tenantSku?.is_active,
      alias_name: tenantSku?.alias_name,
      threshold_qty: tenantSku?.threshold_qty,
      cost_price: tenantSku?.cost_price,
      product_batches: tenantSku?.product_batches || null,
      uom_info: tenantSku?.uom_info,
      on_hand_qty: tenantSku?.product_batches?.reduce((sum, batch) => sum + batch?.on_hand_qty, 0) ?? null,
      internal_sku_code: tenantSku?.internal_sku_code,
    },
    bom_lines: [],
    bom_by_products: null,
    bom_lines_first_level_exploded_view: [],
    bom_route_charges: 0,
    bom_route_charges_per_unit: 0,
    bom_extra_charges_per_unit: 0,
    bom_extra_charges: 0,
    item_total_cost: 0,
    item_total_cost_with_wastage: 0,
    item_unit_cost: 0,
    item_unit_cost_with_wastage: 0,
    bom_total: 0,
    bom_cost: 0,
  };

  return bom;
};

export const createDefaultBomLineData = ({
  bomId, bomLineId, bomInfo, fgInfo, fgTenantProductId, initialCfRMLine,
}) => {
  const newLine = {
    bom_id: bomId,
    bom_line_id: bomLineId,
    bom_line_quantity: 0,
    product_sku_id: null,
    wastage_percentage: 0,
    child_bom_id: null,
    bom_quantity: bomInfo?.bom_quantity,
    parent_bom_id: bomId,
    bom_line_custom_fields: [],
    bom_list: null,
    is_used: true,
    rm_quantity_per_unit_bom: 0,
    item_unit_cost: 0,
    item_unit_cost_with_wastage: 0,
    item_total_cost: 0,
    item_total_cost_with_wastage: 0,
    qty_per_unit: 0,
    total_quantity: 0,
    key: uuidv4(),
    parentKey: null,
    bomLineId,
    quantityPerUnit: 0,
    parentQuantityPerUnit: 0,
    estimatedUsage: 0,
    wastage_quantity: 0,
    fg_tenant_product_id: fgTenantProductId,
    bom_line_parent_bom_id: bomId,
    lineCustomFields: initialCfRMLine,
    fgQuantity: fgInfo?.quantity,
    isAdhocLine: true,
  };

  return newLine;
};

export const createDefaultChildBomLine = ({
  bomId, bomLineId, bomInfo, parentBomId,
}) => {
  const childBomLine = {
    bom_id: bomId,
    bom_line_id: bomLineId,
    bom_line_quantity: 0,
    product_sku_id: null,
    wastage_percentage: 0,
    child_bom_id: null,
    bom_quantity: bomInfo?.bom_quantity,
    parent_bom_id: parentBomId,
    bom_line_custom_fields: [],
    bom_list: null,
    is_used: true,
    rm_quantity_per_unit_bom: 0,
  };

  return childBomLine;
};

export const createBomLineDataFromProductData = ({ tenantSku, productData, initialCfRMLine }) => {
  const bomData = {
    uom_id: tenantSku?.product_info?.uom_id,
    product_sku_id: tenantSku?.product_sku_id,
    uom_info: tenantSku?.uom_info,
    remarks: null,
    product_info: {
      product_sku_id: tenantSku?.product_info?.product_sku_id,
      product_sku_name: tenantSku?.product_info?.product_sku_name,
      expiry_date_format: tenantSku?.product_info?.expiry_date_format,
      manufacturing_date_format: tenantSku?.product_info?.manufacturing_date_format,
      colour: tenantSku?.product_info?.colour,
      is_active: tenantSku?.product_info?.is_active,
      is_perishable: tenantSku?.product_info?.is_perishable,
      expiry_days: tenantSku?.product_info?.expiry_days,
      uom_id: tenantSku?.product_info?.uom_id,
      assets: tenantSku?.product_info?.assets,
      uom_info: tenantSku?.product_info?.uom_info,
      uom_list: tenantSku?.product_info?.uom_list,
      internal_sku_code: tenantSku?.product_info?.internal_sku_code,
      product_batch_counter: tenantSku?.product_info?.product_batch_counter,
      description: tenantSku?.product_info?.description,
      default_outwards_method: tenantSku?.product_info?.default_outwards_method,
      ref_product_code: tenantSku?.product_info?.ref_product_code,
      product_category_info: tenantSku?.product_category_info,
    },
    tenant_product_info: {
      product_sku_id: tenantSku?.product_sku_id,
      tenant_product_id: tenantSku?.tenant_product_id,
      tenant_id: tenantSku?.tenant_id,
      is_active: tenantSku?.is_active,
      alias_name: tenantSku?.alias_name,
      threshold_qty: tenantSku?.threshold_qty,
      cost_price: tenantSku?.cost_price,
      product_batches: tenantSku?.product_batches || null,
      on_hand_qty: tenantSku?.product_batches?.reduce((sum, batch) => sum + batch?.on_hand_qty, 0) ?? null,
      uom_info: tenantSku?.uom_info,
      internal_sku_code: tenantSku?.internal_sku_code,
    },
    productSkuName: tenantSku?.product_info?.product_sku_name,
    product_category_info: tenantSku?.product_category_info,
    tenantProductId: tenantSku?.tenant_product_id,
    currentStock: tenantSku?.product_batches?.reduce((sum, batch) => sum + batch?.on_hand_qty, 0) ?? null,
    costPerUnit: tenantSku?.cost_price,
    uomId: tenantSku?.product_info?.uom_id,
    uomInfo: tenantSku?.product_info?.uom_info,
    quantityPerUnit: 0,
    parentQuantityPerUnit: 0,
    estimatedUsage: 0,
    wastage_quantity: 0,
    lineCustomFields: FormHelpers.lineSystemFieldValue(initialCfRMLine, [])?.map((i) => ({
      ...i,
      // fieldValue: (i?.fieldName === 'Quantity' ? Number(0) : i?.fieldValue),
      fieldValue: (i?.fieldName === 'Quantity' ? Number(0) : (i?.fieldType === 'NUMBERS' ? Number(i?.fieldValue) : i?.fieldValue)),
    })),
  };

  return bomData;
};

export const createChildBomLineDataFromProductData = ({ tenantSku, bomData }) => {
  const childBomLineData = {
    uom_id: tenantSku?.product_info?.uom_id,
    product_sku_id: tenantSku?.product_sku_id,
    uom_info: tenantSku?.uom_info,
    remarks: null,
    product_info: bomData.product_info,
    tenant_product_info: bomData.tenant_product_info,
  };

  return childBomLineData;
};

export const addProductInfoOfRmRowAtChildBomLinesRecursively = ({ bomLines, childBomLineData, bomLineId }) => {
  function insertChildBomLine(lines) {
    lines.forEach((line) => {
      if (line.bom_line_id === bomLineId) {
        Object.assign(line, childBomLineData);
        // line = { ...line, ...childBomLineData };
      } else if (line.child_bom_lines) insertChildBomLine(line.child_bom_lines);
    });
  }

  insertChildBomLine(bomLines);
  return bomLines;
};

export const addNewRmRowAtChildBomLinesRecursively = ({
  bomLines, newLine, childBomLine, childBomId, parentBomLineId, isMultiMode,
}) => {
  function insertChildBomLine(lines) {
    lines.forEach((line) => {
      if (line.bom_line_id === parentBomLineId) {
        line.child_bom_lines = line.child_bom_lines || [];
        line.child_bom_lines.push(...(isMultiMode ? childBomLine : [childBomLine]));
        if (!line.child_bom_id) line.child_bom_id = childBomId;
      } else if (line.child_bom_lines) insertChildBomLine(line.child_bom_lines);
    });
  }

  insertChildBomLine(bomLines);
  bomLines.push(...(isMultiMode ? newLine : [newLine]));
  return bomLines;
};

export const addBomLineIdAtChildBomLinesOfItsParent = ({ bomLines, bomLineId, moLineId }) => {
  function insertChildBomLine(lines) {
    lines.forEach((line) => {
      if (line.mo_line_id === moLineId) {
        Object.assign(line, { bom_line_id: bomLineId, isAdhocLine: true });
      } else if (line.child_bom_lines) insertChildBomLine(line.child_bom_lines);
    });
  }

  insertChildBomLine(bomLines);
  return bomLines;
};

export const getDataByEmptyRowAtLast = (lines) => [...lines.filter((item) => item?.tenant_product_info?.product_sku_id), ...lines.filter((item) => !item?.tenant_product_info?.product_sku_id)];
